import { apiRequest, axiosClient } from './apiClient';
import { ApiError } from '@/models/errors/ApiError';
import type { CompanySettings } from '@/stores/settings';
import type { TemplatePlaceholder } from '@/types/TemplatePlaceholder';
import { formatTimestamp, browserTimezone } from '@/utils/dt';


// Company tab

type CompanySettingsApiResponse = {
    id: number;
    creditor_id: number;
    company_name: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    email: string;
    phone: string;
    fax: string;
    logo: string | null;
    created_at: string;
    updated_at: string;
};

function mapFromApi(data: CompanySettingsApiResponse): CompanySettings {
    return {
        companyName: data.company_name ?? '',
        address1:    data.address ?? '',
        address2:    data.address2 ?? '',
        city:        data.city ?? '',
        state:       data.state ?? '',
        zipCode:     data.zip ?? '',
        email:       data.email ?? '',
        phone:       data.phone ?? '',
        fax:         data.fax ?? '',
        logoUrl:     data.logo ? new URL(data.logo, process.env.VUE_APP_API_BASE_URL).toString() : '',
    };
}

export async function getCompanySettings(): Promise<CompanySettings> {
    const response = await apiRequest<{ data: CompanySettingsApiResponse }>(
        '/api/company/settings',
        'GET',
    );
    return mapFromApi(response.data);
}

export async function saveCompanySettings(
    data: CompanySettings,
    logoFile?: File | null,
): Promise<CompanySettings> {
    try {
        const stateCode = data.state.trim().toUpperCase();

        if (logoFile) {
            const formData = new FormData();
            formData.append('company_name', data.companyName);
            formData.append('address',      data.address1);
            formData.append('address2',     data.address2);
            formData.append('city',         data.city);
            formData.append('state',        stateCode);
            formData.append('zip',          data.zipCode);
            formData.append('email',        data.email);
            formData.append('phone',        data.phone);
            formData.append('fax',          data.fax);
            formData.append('logo_file',    logoFile);

            const response = await axiosClient.post<{ data: CompanySettingsApiResponse }>(
                '/api/company/settings',
                formData,
            );
            return mapFromApi(response.data.data);
        }

        const response = await apiRequest<{ data: CompanySettingsApiResponse }>(
            '/api/company/settings',
            'POST',
            {
                body: {
                    company_name: data.companyName,
                    address:      data.address1,
                    address2:     data.address2,
                    city:         data.city,
                    state:        stateCode,
                    zip:          data.zipCode,
                    email:        data.email,
                    phone:        data.phone,
                    fax:          data.fax,
                },
            },
        );
        return mapFromApi(response.data);
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                company_name: 'companyName',
                address: 'address1',
                zip: 'zipCode',
            });
        }
        throw error;
    }
}

export async function deleteCompanyLogo(): Promise<void> {
    await apiRequest('/api/company/settings/logo', 'DELETE');
}

export async function getSettlementLetterPlaceholders(): Promise<TemplatePlaceholder[]> {
    const response = await apiRequest<{ data: TemplatePlaceholder[] }>(
        '/api/company/settings/settlement-letter-placeholders',
        'GET',
    );
    return response.data;
}
// Users tab

// PHASE1: Only Admin and General are offered when creating/editing users.
export const COMPANY_USER_ROLES = [
    { label: 'Administrator', value: 'admin' },
    { label: 'General', value: 'general' },
];
/*
 * PHASE2/legacy: restore if Accounting/Negotiations need to be creatable again.
export const COMPANY_USER_ROLES = [
    { label: 'Accounting',    value: 'accounting' },
    { label: 'Negotiations',  value: 'negotiations' },
    { label: 'Administrator', value: 'admin' },
];
*/

const COMPANY_USER_ROLE_LABELS: Record<string, string> = {
    accounting: 'Accounting',
    negotiations: 'Negotiations',
    admin: 'Administrator',
    general: 'General',
};

export function roleLabel(code: string): string {
    return COMPANY_USER_ROLE_LABELS[code] ?? code;
}

type CompanyUserApiItem = {
    id: number;
    name: string;
    status: number;
    role: string;
    email: string;
    phone: string | null;
    created_date: number | null;
    last_login_date: number | null;
};

type CompanyUsersApiResponse = {
    data: CompanyUserApiItem[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
    };
};

type CompanyUserApiResponse = {
    data: CompanyUserApiItem;
};

export type CompanyUser = {
    id: number;
    userName: string;
    status: 'Active' | 'Inactive' | 'Invited';
    role: string;
    email: string;
    phone: string;
    createdDate: string;
    lastLoginDate: string;
};

export type CreateCompanyUserInput = {
    role: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

function normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
}

function mapCompanyUser(user: CompanyUserApiItem): CompanyUser {
    const tz = browserTimezone();
    return {
        id:            user.id,
        userName:      user.name,
        status:        user.status === 1 ? 'Active' : user.status === 2 ? 'Invited' : 'Inactive',
        role:          user.role ?? '',
        email:         user.email,
        phone:         user.phone ?? '',
        createdDate:   user.created_date   ? formatTimestamp(new Date(user.created_date   * 1000), tz) : '',
        lastLoginDate: user.last_login_date ? formatTimestamp(new Date(user.last_login_date * 1000), tz) : '',
    };
}

export async function getCompanyUsers(
    limit = 100,
    offset = 0,
    status?: 0 | 1 | 2,
    sort?: string,
    order?: 'ASC' | 'DESC',
    role?: 'accounting' | 'negotiations' | 'admin' | 'general', // PHASE1: role filter
): Promise<{ users: CompanyUser[]; total: number }> {
    const params: Record<string, string | number> = { limit, offset };
    if (status !== undefined) params.status = status;
    if (sort) params.sort = sort;
    if (order) params.order = order;
    if (role) params.role = role;

    const response = await apiRequest<CompanyUsersApiResponse>(
        '/api/company/settings/user',
        'GET',
        { params },
    );
    return {
        users: response.data.map(mapCompanyUser),
        total: response.pagination.total,
    };
}

export async function createCompanyUser(data: CreateCompanyUserInput): Promise<CompanyUser> {
    try {
        const response = await apiRequest<CompanyUserApiResponse>(
            '/api/company/settings/user',
            'POST',
            {
                body: {
                    role: data.role,
                    first_name: data.firstName.trim(),
                    last_name: data.lastName.trim(),
                    email: data.email.trim(),
                    phone: normalizePhone(data.phone),
                },
            },
        );

        return mapCompanyUser(response.data);
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                first_name: 'firstName',
                last_name: 'lastName',
            });
        }

        throw error;
    }
}

export type UpdateCompanyUserInput = {
    active: 0 | 1;
    role: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

export async function updateCompanyUser(id: number, data: UpdateCompanyUserInput): Promise<CompanyUser> {
    try {
        const response = await apiRequest<CompanyUserApiResponse>(
            `/api/company/settings/user/${id}`,
            'PUT',
            {
                body: {
                    active:      data.active,
                    role:        data.role,
                    first_name:  data.firstName.trim(),
                    last_name:   data.lastName.trim(),
                    email:       data.email.trim(),
                    phone:       normalizePhone(data.phone),
                },
            },
        );

        return mapCompanyUser(response.data);
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                first_name: 'firstName',
                last_name:  'lastName',
                active:     'status',
            });
        }

        throw error;
    }
}

export async function deactivateCompanyUser(
    id: number,
    data: { role: string; userName: string; email: string; phone: string },
): Promise<void> {
    const [firstName, ...rest] = data.userName.trim().split(' ');
    const lastName = rest.join(' ');
    await apiRequest(
        `/api/company/settings/user/${id}`,
        'PUT',
        {
            body: {
                active:      0,
                role:        data.role,
                first_name:  firstName,
                last_name:   lastName,
                email:       data.email,
                phone:       normalizePhone(data.phone) || undefined, // API requires phone, dummy value for now
            },
        },
    );
}

export async function activateCompanyUser(
    id: number,
    data: { role: string; userName: string; email: string; phone: string },
): Promise<void> {
    const [firstName, ...rest] = data.userName.trim().split(' ');
    const lastName = rest.join(' ');
    await apiRequest(
        `/api/company/settings/user/${id}`,
        'PUT',
        {
            body: {
                active:      1,
                role:        data.role,
                first_name:  firstName,
                last_name:   lastName,
                email:       data.email,
                phone:       normalizePhone(data.phone) || undefined,
            },
        },
    );
}

export async function resendUserInvitation(id: number): Promise<void> {
    await apiRequest(
        `/api/company/settings/user/${id}/resend-invitation`,
        'POST',
    );
}

export async function deleteCompanyUser(id: number): Promise<void> {
    await apiRequest(
        `/api/company/settings/user/${id}`,
        'DELETE',
    );
}
